import { Button, ButtonTheme } from 'components/Button';
import { Card } from 'components/Card';
import { OptionField } from 'components/Field/OptionField';
import { ICONS } from 'components/Icon';
import { ModalScreenProps, ModalScreens } from 'components/Navigation/types';
import { NavigationHeader } from 'components/NavigationHeader';
import { PageScrollView } from 'components/Page';
import { useEsc } from 'hooks/useEsc';
import { useSelector } from 'hooks/useSelector';
import { theme } from 'lib/theme';
import { isEqual } from 'lodash';
import React from 'react';
import { useDispatch } from 'react-redux';
import { NAVIGATE_MODAL_BACK } from 'store/actions';
import {
  doEngageFlatsLoad,
  doEngageFlatsSetFilter,
} from 'store/remote/engageActions';
import {
  selectEngageFlatsFilterOccupationStatuses,
  selectEngageFlatsFilterStatuses,
} from 'store/remote/engageSelectors';
import {
  EngageFlatsDefaultFilter,
  EngageFlatsFilterStatus,
  EngageFlatsOccupationStatusFilters,
  EngageFlatsStatusFilters,
  FlatOccupationStatus,
  OccupationStatusIcons,
} from 'types/Engage';

const surveyStatusIconColor: Record<EngageFlatsFilterStatus, string> = {
  complete: theme.colors.green,
  incomplete: theme.colors.red,
};

const allOccupationStatusIds = EngageFlatsOccupationStatusFilters.map(
  (status) => status.id,
);

export const EngageFlatsFilterModal: React.FC<
  ModalScreenProps<ModalScreens.EngageFlatsFilter>
> = ({ route }) => {
  const dispatch = useDispatch();
  const { pageKey, propertyId } = route.params;

  const currentStatuses = useSelector((state) =>
    selectEngageFlatsFilterStatuses(state, pageKey),
  );
  const currentOccupationStatuses = useSelector((state) =>
    selectEngageFlatsFilterOccupationStatuses(state, pageKey),
  );

  const [statuses, setStatuses] = React.useState(currentStatuses);
  const [occupationStatuses, setOccupationStatuses] = React.useState(
    currentOccupationStatuses,
  );

  const handleClose = React.useCallback(() => {
    dispatch(NAVIGATE_MODAL_BACK());
  }, [dispatch]);

  const handleApply = React.useCallback(() => {
    dispatch(
      doEngageFlatsSetFilter({
        key: pageKey,
        data: {
          statuses,
          occupationStatuses,
        },
      }),
    );
    dispatch(
      doEngageFlatsLoad({
        key: pageKey,
        propertyId,
        page: 'reset',
      }),
    );
    handleClose();
  }, [
    dispatch,
    handleClose,
    occupationStatuses,
    pageKey,
    propertyId,
    statuses,
  ]);

  const handleClear = React.useCallback(() => {
    setStatuses(EngageFlatsDefaultFilter.statuses);
    setOccupationStatuses(EngageFlatsDefaultFilter.occupationStatuses);
  }, []);

  const handleSurveyStatusChange = React.useCallback((values: string[]) => {
    setStatuses(values as EngageFlatsFilterStatus[]);
  }, []);

  const handleOccupationStatusChange = React.useCallback((values: string[]) => {
    setOccupationStatuses(values as FlatOccupationStatus[]);
  }, []);

  const occupationBadgeActive =
    occupationStatuses.length !== allOccupationStatusIds.length;

  const isDefaultSelection =
    isEqual(statuses, EngageFlatsDefaultFilter.statuses) &&
    isEqual(occupationStatuses, EngageFlatsDefaultFilter.occupationStatuses);

  useEsc(handleClose);

  return (
    <PageScrollView
      header={
        <NavigationHeader
          heading="Flats Filter"
          onPressCancelButton={handleClose}
          cancelButtonText="Close"
          onPressSaveButton={handleApply}
          saveButtonText="Filter"
        />
      }
    >
      <Card marginBottomHalf>
        <OptionField
          label="Survey Status"
          modalLabel="Survey Status"
          onChange={handleSurveyStatusChange}
          value={statuses}
          noValueOption={{
            name: 'All Survey Statuses',
          }}
          showBadge={!!statuses.length}
          options={EngageFlatsStatusFilters.map((status) => ({
            name: status.name,
            internalValue: status.id,
            icon: ICONS.FileDuotone,
            iconColor: surveyStatusIconColor[status.id],
          }))}
          showLabelInCloseHeader
          labelAlignTop
          expandedInlineFirst
          modalSize="small"
          showFieldTypeIcon={false}
          clearable
        />
        <OptionField
          label="Occupation Status"
          modalLabel="Occupation Status"
          onChange={handleOccupationStatusChange}
          value={occupationStatuses}
          multiSelect
          modalEnableSelectAll
          showBadge={occupationBadgeActive}
          options={EngageFlatsOccupationStatusFilters.map((status) => ({
            name: status.name,
            internalValue: status.id,
            icon: OccupationStatusIcons[status.id],
          }))}
          showLabelInCloseHeader
          labelAlignTop
          expandedInlineLast
          modalSize="small"
          showFieldTypeIcon={false}
          modalShowQuickChoose
        />
      </Card>

      <Button
        text="Clear Filters"
        buttonTheme={ButtonTheme.link}
        style={{
          alignSelf: 'center',
          marginVertical: theme.paddingPageVertical,
        }}
        onPress={handleClear}
        disabled={isDefaultSelection}
      />
    </PageScrollView>
  );
};
